import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { ShieldCheck, KeyRound, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Secondo fattore di autenticazione (TOTP / Google Authenticator).
 *
 * Props:
 *  - mfaToken : token breve restituito da /auth/login o /auth/register
 *  - mode     : 'mfa'    -> l'utente ha già la 2FA attiva, chiede solo il codice
 *               'enroll' -> primo accesso: mostra QR + secret, poi attiva
 *  - email    : mostrato come promemoria (opzionale)
 *  - onSuccess(data) : data = { access_token, user }  (login completato)
 *  - onCancel()      : torna al form credenziali
 */
const TwoFactorChallenge = ({ mfaToken, mode, email, onSuccess, onCancel }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState(null); // { qr_png, secret, otpauth_uri }
  const [loadingSetup, setLoadingSetup] = useState(mode === 'enroll');

  useEffect(() => {
    if (mode !== 'enroll') return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(
          `${API}/auth/2fa/setup`,
          {},
          { headers: { Authorization: `Bearer ${mfaToken}` } }
        );
        if (!cancelled) setSetup(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Errore durante l\'avvio della configurazione 2FA');
        if (!cancelled && onCancel) onCancel();
      } finally {
        if (!cancelled) setLoadingSetup(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, mfaToken]);

  const submit = async (e) => {
    e.preventDefault();
    const clean = code.replace(/\s/g, '');
    if (clean.length < 6) {
      toast.error('Inserisci il codice a 6 cifre dell\'app');
      return;
    }
    setBusy(true);
    try {
      const url = mode === 'enroll' ? `${API}/auth/2fa/activate` : `${API}/auth/login/2fa`;
      const r = await axios.post(url, { mfa_token: mfaToken, code: clean });
      onSuccess(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Codice non valido');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-lg">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6" />
          {mode === 'enroll' ? 'Configura la verifica in due passaggi' : 'Verifica in due passaggi'}
        </CardTitle>
        <CardDescription className="text-slate-500">
          {mode === 'enroll'
            ? 'Scansiona il QR con Google Authenticator (o app compatibile), poi inserisci il codice generato.'
            : `Inserisci il codice a 6 cifre dell'app di autenticazione${email ? ` per ${email}` : ''}.`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {mode === 'enroll' && (
          <div className="mb-5">
            {loadingSetup ? (
              <div className="flex items-center justify-center py-10 text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Generazione QR…
              </div>
            ) : setup ? (
              <div className="flex flex-col items-center gap-3">
                <img
                  src={setup.qr_png}
                  alt="QR code 2FA"
                  className="w-44 h-44 border border-slate-200 rounded-lg bg-white"
                />
                <div className="text-center">
                  <p className="text-xs text-slate-500 mb-1">Oppure inserisci manualmente questa chiave:</p>
                  <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded break-all">
                    {setup.secret}
                  </code>
                </div>
              </div>
            ) : (
              <p className="text-sm text-red-600">Impossibile generare il QR. Riprova dal login.</p>
            )}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="totp-code" className="text-slate-700">Codice di verifica</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="totp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                className="pl-10 tracking-[0.4em] text-center text-lg"
                data-testid="totp-code-input"
                autoFocus
                required
              />
            </div>
          </div>
          <Button
            type="submit"
            className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-2.5 rounded-lg transition-all active:scale-95"
            disabled={busy || (mode === 'enroll' && !setup)}
            data-testid="totp-submit-btn"
          >
            {busy ? 'Verifica…' : mode === 'enroll' ? 'Attiva e accedi' : 'Verifica e accedi'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-slate-600 hover:text-slate-900 hover:underline"
            data-testid="totp-cancel-btn"
          >
            ← Torna indietro
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TwoFactorChallenge;
